import { useMemo } from 'react'
import { useFeatureFlag } from '../../React/Common/Hooks/useFeatureFlag'
import { PermissionsUtil } from '../../React/Common/Utils/PermissionsUtil'
import { IsVarEmpty } from '../../NonReact/utils/GlobalFunctions'
import { RxJSCurrentState, useRxJSState } from '../../React/Common/Hooks/useRxJSState'
import { currentUserState$ } from '../../NonReact/common/RxJS/UserInfoState'
import { AdminUserResponse } from '../../NonReact/common/Types/Types'

/**
 * A hook that determines whether the presence page is available to the user.
 *
 * Presence eligibility that depends on the TelSys (type, comms enabled, integration type/mode/options
 * — business mode requires the CX-Analytics option and Real-Time — and the reporting licence level) is
 * computed server-side and surfaced as {@link AdminUserResponse.CanAccessPresence}, so the icon cannot
 * drift from the backend route guard (`CActionGenericReactPage.canAccessPresence`). The remaining
 * conditions here are client-side concerns: the presence feature flag, CME-wide users (never scoped to
 * a single Teams TelSys) and the per-user presence-board disable.
 */
export const useIsPresenceAvailable = (): Definable<boolean> => {
    const [userInfo]: RxJSCurrentState<AdminUserResponse> = useRxJSState(currentUserState$)

    const showPresencePageFlag: Definable<boolean> = useFeatureFlag('showTeamsPresencePage')

    return useMemo((): Definable<boolean> => {
        if (IsVarEmpty(userInfo) || IsVarEmpty(showPresencePageFlag)) {
            return undefined
        }

        return (
            showPresencePageFlag &&
            !PermissionsUtil.isCMEWideUser(userInfo) &&
            (userInfo.CanAccessPresence ?? false) &&
            !userInfo.DisablePresenceBoard
        )
    }, [userInfo, showPresencePageFlag])
}
